import React, { FC } from 'react';
import produce from 'immer';
import { get, merge } from 'lodash';
import { useTranslation } from 'react-i18next';
import { FormItemInjectedProps } from '~/types/FormItemInjectedProps';
import { ApplicationPayload } from '~/types/ApplicationPayload';
import FormDrawer from '~/components/FormDrawer';
import { KsaPerson } from '~/types/ksa/KsaPerson';
import { Form } from '~/components';
import { useValidator } from '~/controllers/validation';
import { Stage } from '~/types/Application';
import { useGetApplication, useGetPayload } from '~/controllers/wizard';
import { useGetDraft } from '~/controllers/draft';
import { Draft } from '~/types/Draft';
import { useOptionalFields } from '~/hooks/useOptionalFields';
import { getStagePayload } from '~/utils/getStagePayload';
import { FormItemContextProvider } from '~/components/Form/FormItemContext';
import { isReadOnly } from '~/utils/isReadOnly';
import { isEditButtonDisabled } from '~/components/views/ShareholdersMultiple/utils';
import {
  managersGetFormName,
  hasAnyDeclinedField,
} from '~/pages/ksa/application/managers/components/ManagerEdit';
import PersonReviewFormItems from './PersonReviewFormItems';

/** Returns true when the Manager drawer fields should be editable.
 *  This covers both the Review stage and the Contract stage so that
 *  agents can update manager data at either point in the flow. */
const isManagerEditable = (application: ReturnType<typeof useGetApplication>['data']): boolean => {
  if (application?.stage === Stage.ksaContract) return true;
  return !isReadOnly(application);
};

interface Props extends Partial<FormItemInjectedProps<{ managers: KsaPerson[] }>> {
  uid: string;
  onClose: () => void;
}

const ManagerReviewEdit: FC<Props> = (props) => {
  const { uid, onClose } = props;
  const { value, onChange } = props as FormItemInjectedProps<{ managers: KsaPerson[] }>;
  const { t } = useTranslation('ksaReview');
  const reviewManagersFormState = { reviewManagers: value };
  const applicationQuery = useGetApplication();
  const payload = useGetPayload();
  const currentIndex = payload?.managers?.managers?.findIndex((item) => item?.uid === uid) || 0;

  const onFinish = (newValues: ApplicationPayload) => {
    const updatedData = produce(reviewManagersFormState, (draft) => {
      merge(draft, newValues);
    });
    onChange?.(updatedData.reviewManagers);
    onClose();
  };

  const withContinueButton =
    applicationQuery.data?.stage === Stage.ksaContract ||
    !isEditButtonDisabled(applicationQuery.data, hasAnyDeclinedField(applicationQuery?.data));

  return (
    <FormDrawer
      id="managerReviewDrawer"
      visible={!!uid}
      onClose={onClose}
      title={t('Manager')}
      formName={reviewManagersFormName}
      withContinueButton={withContinueButton}
    >
      <ManagerForm
        index={currentIndex}
        onFinish={onFinish}
        initialValues={reviewManagersFormState}
      />
    </FormDrawer>
  );
};

interface ManagerFormProps {
  index: number;
  onFinish: (values: ApplicationPayload) => any;
  initialValues?: any | undefined;
}

const ManagerForm: FC<ManagerFormProps> = (props) => {
  const { onFinish, initialValues, index } = props;

  const [form] = Form.useForm<ApplicationPayload>();
  const getFormState = () => {
    return produce(initialValues || {}, (draft: any) => {
      merge(draft, form.getFieldsValue());
    });
  };
  const validatorController = useValidator(getFormState, undefined, Stage.ksaApplication);
  const applicationQuery = useGetApplication();
  const payload = useGetPayload();
  const draftQuery = useGetDraft<Draft<any>>();

  const optionalFieldsController = useOptionalFields({ initialValues });

  const stagePayload = getStagePayload.wizard(applicationQuery.data);
  const managerFromProvided: KsaPerson | undefined = get(
    payload?.provided,
    managersGetFormName(index)
  );
  const managerFromApplication: KsaPerson | undefined = get(
    applicationQuery?.data?.payload,
    managersGetFormName(index)
  );

  return (
    <FormItemContextProvider
      value={{
        defaultValidator: validatorController.validator,
        applicationPayload: applicationQuery.data?.payload,
        draftPayload: draftQuery.data,
        optionalFields: optionalFieldsController.optionalFields || {},
        errorFields: undefined,
        enabledFields: stagePayload?.activeStep.errorInformation?.enabledFields,
        disabledByDefault: !isManagerEditable(applicationQuery.data),
        disabled: !isManagerEditable(applicationQuery.data),
      }}
    >
      <Form
        form={form}
        name={reviewManagersFormName}
        onFinish={onFinish}
        onValuesChange={(_: any, newValues: any) => {
          optionalFieldsController.debouncedUpdate(newValues);
        }}
        initialValues={initialValues}
      >
        <PersonReviewFormItems
          formItemsPath={reviewManagersGetFormName(index)}
          payloadReferencePath={managersGetFormName(index)}
          hasPassport={!!managerFromProvided?.passport?.documentNumber}
          hasPersonalDocument={!!managerFromProvided?.personalDocument?.idNumber}
          isSignedWithEmail={!!managerFromProvided?.personalData?.email}
          hasVerifiedPhoneNumber={
            !!managerFromProvided?.personalData?.phoneNumber ||
            !!managerFromApplication?.personalData?.phoneNumber
          }
          showRelationshipType={true}
        />
      </Form>
    </FormItemContextProvider>
  );
};

export const reviewManagersFormName = 'reviewManagers';
export const reviewManagersGetFormName = (index: number) => [
  reviewManagersFormName,
  'managers',
  index,
];

export default ManagerReviewEdit;
